import { Helmet } from 'react-helmet-async';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api.js';
import { queryKeys } from '../lib/queryKeys.js';
import { useProducts, useBrands, useCollections } from '../features/catalog/queries.js';
import { useAddToCart } from '../features/cart/useCart.js';
import { PromoBanner } from '../components/commerce/PromoBanner.jsx';
import { Section, SectionHeading } from '../components/layout/Section.jsx';
import { HomeHero } from '../components/home/HomeHero.jsx';
import { HeroSlider, showableSlides } from '../components/home/HeroSlider.jsx';
import { HomeMarquee } from '../components/home/HomeMarquee.jsx';
import { ProductGrid } from '../components/home/ProductGrid.jsx';
import { CollectionShelf } from '../components/home/CollectionShelf.jsx';
import { BrandStory } from '../components/home/BrandStory.jsx';
import { SportCompare } from '../components/home/SportCompare.jsx';
import { HomeReviews } from '../components/home/HomeReviews.jsx';
import { HomeBrands } from '../components/home/HomeBrands.jsx';
import { HomeJournal } from '../components/home/HomeJournal.jsx';
import { useReducedMotion } from '../hooks/useReducedMotion.js';
import { env } from '../lib/env.js';

/**
 * The home page.
 *
 * One long, centred document in the order a shopper reads a shop window: the
 * banner and the strip of store facts under it, then shelves — newest, one per
 * collection, the store's own story, what sells — and only then the
 * offer, the comparison, reviews, the houses and the journal.
 *
 * Every shelf is a real catalogue query and every line of copy that is not a
 * plain label comes from settings, so the page an operator sees is the page
 * they curated. Shelves that come back empty either say what to do next or step
 * aside, never leave a hole.
 */

// Shelves stacked one after another share a single gap rather than doubling
// it: each one after the first drops its top padding and leans on the bottom
// padding of the shelf above.
const SHELF_FOLLOWS = 'pt-0 lg:pt-0';

export const Home = () => {
  const reduced = useReducedMotion();
  const addToCart = useAddToCart();

  const featured = useProducts({ featured: 'true', limit: 8 });
  const latest = useProducts({ sort: 'newest', limit: 10 });
  const bestSellers = useProducts({ sort: 'popular', limit: 10 });
  const sportHeroes = useProducts({ world: 'sport', sort: 'popular', limit: 3 });
  const { data: brands } = useBrands({ activeOnly: 'true' });
  // Published collections, in the order the admin panel sets.
  const { data: collections } = useCollections();

  // The banner's words, the strip's facts and the brand story all live in
  // settings, so they change without a deploy.
  const { data: settings, isPending: settingsPending } = useQuery({
    queryKey: queryKeys.settings.public(),
    queryFn: () => api.get('/settings/public'),
    staleTime: 15 * 60 * 1000,
  });

  const { data: journal } = useQuery({
    queryKey: queryKeys.blog.list({ limit: 3 }),
    queryFn: () => api.get('/blog', { params: { limit: 3 } }),
    staleTime: 10 * 60 * 1000,
  });

  const { data: reviewWall } = useQuery({
    queryKey: ['home', 'top-rated'],
    queryFn: () => api.get('/products', { params: { sort: 'rating', limit: 6 } }),
    staleTime: 10 * 60 * 1000,
  });

  // Uploaded banner artwork, when there is any, takes the top of the page in
  // place of the type-and-shelf banner.
  const heroSlides = showableSlides(settings?.home?.heroSlides);

  const quickAdd = (product) =>
    product.type === 'simple' ? () => addToCart.mutate({ productId: product._id }) : undefined;

  return (
    <>
      <Helmet>
        <title>Golden Era — fragrance and sports equipment</title>
        <meta
          name="description"
          content="Two collections under one masthead. Every specification on every page, so you can compare before you commit."
        />
        <link rel="canonical" href={env.siteUrl} />
        <meta property="og:title" content="Golden Era — fragrance and sports equipment" />
        <meta property="og:type" content="website" />
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Organization',
            name: 'Golden Era',
            url: env.siteUrl,
            description: 'Fragrance and sports equipment, chosen carefully and sold plainly.',
          })}
        </script>
      </Helmet>

      {/* 1 ───────────────────────────────────────────────────────── banner */}
      {/* Until settings answer, which banner to draw is unknown: a neutral box
          holds the space rather than flashing one banner and swapping it. */}
      {settingsPending ? (
        <div aria-hidden="true" className="aspect-[1920/700] w-full bg-cream motion-safe:animate-pulse md:max-h-[calc(100svh-7rem)]" />
      ) : heroSlides.length > 0 ? (
        <HeroSlider slides={heroSlides} />
      ) : (
        <HomeHero
          copy={settings?.home ?? {}}
          products={featured.data}
          isPending={featured.isPending}
          reduced={reduced}
        />
      )}

      {/* 2 ─────────────────────────────────────────────── store-facts strip */}
      <HomeMarquee settings={settings} />

      {/* 3 ────────────────────────────────────────────────── just launched */}
      <Section>
        <SectionHeading
          eyebrow="Latest drops"
          title="Just launched"
          action={{ label: 'See all new', to: '/shop?sort=newest' }}
        />
        <ProductGrid
          products={latest.data}
          isPending={latest.isPending}
          onQuickAdd={quickAdd}
          priority
          empty={{
            title: 'The shelves are still being filled',
            description: 'Nothing is published yet. New products appear here the moment they go live.',
            action: { label: 'Browse everything', to: '/shop' },
          }}
        />
      </Section>

      {/* 4 ─────────────────────────────────────────── one per collection */}
      {(collections ?? []).map((collection) => (
        <CollectionShelf
          key={collection.slug}
          collection={collection}
          onQuickAdd={quickAdd}
          className={SHELF_FOLLOWS}
        />
      ))}

      {/* 5 ──────────────────────────────────────────────────── brand story */}
      <BrandStory store={settings?.store} className={SHELF_FOLLOWS} />

      {/* 6 ──────────────────────────────────────────────────── bestsellers */}
      <Section className={SHELF_FOLLOWS}>
        <SectionHeading
          eyebrow="Customer favourites"
          title="Bestseller products"
          action={{ label: 'See all', to: '/shop?sort=popular' }}
        />
        <ProductGrid
          products={bestSellers.data}
          isPending={bestSellers.isPending}
          onQuickAdd={quickAdd}
          empty={{
            title: 'Nothing has sold yet',
            description: 'Once orders come in, this shelf fills with what people actually buy.',
            action: { label: 'Browse everything', to: '/shop' },
          }}
        />
      </Section>

      {/* 7 ──────────────────────────────────────────────────── offer band */}
      <PromoBanner promotions={settings?.promotions ?? {}} />

      {/* 8 ─────────────────────────────────────────── sport comparison */}
      <SportCompare products={sportHeroes.data} />

      {/* 9 ────────────────────────────────────────────────── reviews wall */}
      <HomeReviews products={reviewWall} />

      {/* 10 ─────────────────────────────────────────────────── the brands */}
      <HomeBrands brands={brands} />

      {/* 11 ────────────────────────────────────────────────────── journal */}
      <HomeJournal posts={journal} />

      {/* 12 — the footer sitemap and newsletter capture live in Footer.jsx,
             which every page shares. */}
    </>
  );
};

export default Home;
